import { useCallback, useEffect, useRef } from 'react'

import { AnalyticsEvents, track } from '@/lib/services/analytics'
import type { CollectionWizardAnalyticsParams } from '@/types/components/library/collectionWizardAnalytics'

/** Analytics cannot interrupt saving or closing a collection. Never pass names, rules or tokens. */
function emit(event: string, properties: Record<string, string | boolean>) {
  try {
    track(event, properties)
  } catch {
    // Telemetry is best effort and never changes the collection's outcome.
  }
}

/** One mounted dialog session. Only an explicit close records abandonment, never effect cleanup. */
export function useCollectionWizardAnalytics(params: CollectionWizardAnalyticsParams) {
  const session = useRef({ mode: params.mode, entry: params.entry })
  const latest = useRef(params)
  latest.current = params
  const opened = useRef(false)
  const viewedStep = useRef<string | null>(null)
  const terminal = useRef<'saved' | 'closed' | null>(null)

  useEffect(() => {
    if (!opened.current) {
      opened.current = true
      emit(AnalyticsEvents.COLLECTION_WIZARD_OPENED, session.current)
    }
    if (viewedStep.current !== params.step) {
      viewedStep.current = params.step
      emit(AnalyticsEvents.COLLECTION_WIZARD_STEP_VIEWED, { ...session.current, step: params.step })
    }
  }, [params.step])

  const saved = useCallback(() => {
    if (terminal.current) return
    terminal.current = 'saved'
    if (session.current.mode === 'create') emit(AnalyticsEvents.COLLECTION_CREATED, session.current)
  }, [])

  // Called once by the create POST's rejection handler; each retried POST is a new attempt.
  const failed = useCallback(() => {
    if (!terminal.current && session.current.mode === 'create') {
      emit(AnalyticsEvents.COLLECTION_CREATE_FAILED, session.current)
    }
  }, [])

  const close = useCallback(() => {
    if (terminal.current) return
    terminal.current = 'closed'
    const current = latest.current
    emit(AnalyticsEvents.COLLECTION_WIZARD_ABANDONED, {
      ...session.current,
      last_step: current.step,
      had_name: current.hadName,
      had_rules: current.hadRules,
      include_bucket: current.includeCount === 0 ? '0' : current.includeCount === 1 ? '1' : '2+',
    })
  }, [])

  return { saved, failed, close }
}
